import { Event as SentryEvent } from "@sentry/vue";

// CR-300: the guest-join token identifies a specific therapy appointment —
// strip it out of anything Sentry captures (URLs, breadcrumbs, the pageload
// transaction's route params) as defence in depth alongside the URL scrub in
// public/index.html.
const GUEST_JOIN_TOKEN_PATTERN = /\/join\/[^/?#\s"]+/g;
const REDACTED = "[redacted]";
const REDACTED_GUEST_JOIN_PATH = `/join/${REDACTED}`;

// Salesforce-derived values that must never reach Sentry from the guest route:
// room_sid encodes the SF appointment id, and a participant's Twilio identity
// is the clinician's SF object id.
const GUEST_FORBIDDEN_TAGS = ["room_sid"];
const GUEST_FORBIDDEN_KEYS = [
  "identity",
  "token",
  "guest_session_token",
  "challenge_token",
  "roomSid",
  "room_sid",
];

let inGuestSession = false;

// GuestJoin.vue owns this: route-sniffing the event isn't reliable once the
// URL has been scrubbed back to a bare "/join".
export const setGuestSession = (active: boolean): void => {
  inGuestSession = active;
};

export const isGuestSession = (): boolean => inGuestSession;

export const scrubGuestJoinToken = (value: string): string =>
  value.replace(GUEST_JOIN_TOKEN_PATTERN, REDACTED_GUEST_JOIN_PATH);

const scrubRecord = (
  record: Record<string, unknown>,
): Record<string, unknown> => {
  const scrubbed: Record<string, unknown> = {};
  Object.keys(record).forEach((key) => {
    const value = record[key];
    if (GUEST_FORBIDDEN_KEYS.includes(key)) {
      scrubbed[key] = REDACTED;
    } else if (typeof value === "string") {
      scrubbed[key] = scrubGuestJoinToken(value);
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      scrubbed[key] = scrubRecord(value as Record<string, unknown>);
    } else {
      scrubbed[key] = value;
    }
  });
  return scrubbed;
};

export const scrubGuestJoinEvent = (event: SentryEvent): SentryEvent => {
  if (event.request?.url) {
    event.request.url = scrubGuestJoinToken(event.request.url);
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
      if (breadcrumb.message) {
        breadcrumb.message = scrubGuestJoinToken(breadcrumb.message);
      }
      if (breadcrumb.data) {
        breadcrumb.data = scrubRecord(breadcrumb.data);
      }
      return breadcrumb;
    });
  }

  // BrowserTracing records the matched route's params on the pageload
  // transaction, which captures the token even though the address bar no
  // longer shows it.
  const traceData = event.contexts?.trace?.data as
    | Record<string, unknown>
    | undefined;
  if (traceData) {
    (event.contexts as Record<
      string,
      Record<string, unknown>
    >).trace.data = scrubRecord(traceData);
  }

  if (!inGuestSession) return event;

  if (event.tags) {
    GUEST_FORBIDDEN_TAGS.forEach((tag) => {
      if (event.tags && tag in event.tags) delete event.tags[tag];
    });
  }
  if (event.extra) {
    event.extra = scrubRecord(event.extra);
  }

  return event;
};
